import React, { useState } from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';

import { useGetCarouselMetrics } from '~/hooks/apis/experiences';

import { CarouselMetric } from '~/services/ExperiencesService';

import { formatCurrency, formatNumber } from '../utils';

type SortField = 'carouselName' | 'views' | 'clicks' | 'orders' | 'grossSales' | 'margin';
type SortDirection = 'asc' | 'desc';

function getSortValue(metric: CarouselMetric, field: SortField): number | string {
  const m = metric.metrics7d?.last7d;
  switch (field) {
    case 'carouselName':
      return metric.carouselName;
    case 'views':
      return m?.sessionsWithViews ?? 0;
    case 'clicks':
      return m?.clicks ?? 0;
    case 'orders':
      return m?.orders ?? 0;
    case 'grossSales':
      return m?.grossSalesAud ?? 0;
    case 'margin':
      return m?.marginAud ?? 0;
    default:
      return 0;
  }
}

export default function CarouselMetricsTab() {
  const { data: metrics = [], isLoading, error } = useGetCarouselMetrics();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('orders');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filtered = metrics.filter(
    (m) => !searchTerm.trim() || m.carouselName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sorted = [...filtered].sort((a, b) => {
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);
    const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal as string) : (aVal as number) - (bVal as number);
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error instanceof Error ? error.message : 'Failed to fetch carousel metrics'}</Alert>
    );
  }

  if (metrics.length === 0) {
    return <Alert severity="info">No carousel metrics available yet. Data syncs daily at 6am Sydney.</Alert>;
  }

  const updatedAt = metrics[0]?.updatedAt
    ? new Date(metrics[0].updatedAt).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })
    : 'N/A';

  return (
    <Stack direction="column" spacing={3}>
      <Typography variant="h5" fontWeight="bold">
        Carousel Metrics (Last 7 Days)
      </Typography>

      <Stack direction="row" spacing={3} alignItems="center">
        <Box maxWidth={400} flexGrow={1}>
          <TextField
            fullWidth
            label="Search carousels"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {sorted.length} carousel{sorted.length !== 1 ? 's' : ''} | Last sync: {updatedAt}
        </Typography>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <SortableHeader
              field="carouselName"
              label="Carousel"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <SortableHeader
              field="views"
              label="Views"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="clicks"
              label="Clicks"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="orders"
              label="Orders"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="grossSales"
              label="Gross Sales (AUD)"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <SortableHeader
              field="margin"
              label="Margin (AUD)"
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              align="right"
            />
            <TableCell align="right">
              <Typography fontWeight="bold" variant="body2">
                Days w/ Views
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((metric) => {
            const m = metric.metrics7d?.last7d;
            return (
              <TableRow key={metric.carouselName} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {metric.carouselName}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(m?.sessionsWithViews ?? 0)}</TableCell>
                <TableCell align="right">{formatNumber(m?.clicks ?? 0)}</TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{formatNumber(m?.orders ?? 0)}</Typography>
                </TableCell>
                <TableCell align="right">{formatCurrency(m?.grossSalesAud ?? 0)}</TableCell>
                <TableCell align="right">{formatCurrency(m?.marginAud ?? 0)}</TableCell>
                <TableCell align="right">{m?.daysWithViews ?? 0}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Stack>
  );
}

function SortableHeader({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}) {
  return (
    <TableCell align={align}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : 'desc'}
        onClick={() => onSort(field)}
      >
        <Typography fontWeight="bold" variant="body2">
          {label}
        </Typography>
      </TableSortLabel>
    </TableCell>
  );
}
